import Phaser from "phaser";
import {
  DEFAULT_BUTTON_PRESS_TIMING,
  type ButtonPressTiming,
  type ButtonStyleToken,
} from "./tokens.js";

/**
 * `NineSliceButton` 발화 이벤트 키.
 * - `Click`: `pointerup`이 버튼 영역 안에서 일어났을 때만 emit (touch UX 표준 — 영역 밖 드래그 후 떼면 취소).
 */
export const NINE_SLICE_BUTTON_EVENT = {
  Click: "ninesliceButton:click",
} as const;

/**
 * 전역 클릭 사운드 hook — DIP. 패키지는 사운드에 의존하지 않고(의존 0) 이 콜백만 호출한다.
 * 앱이 부팅 시 `setNineSliceButtonClickSound(() => soundController.play("ui.button"))`로 1회 주입하면
 * 모든 NineSliceButton의 유효 클릭(영역 안 pointerup + enabled)에서 자동 재생된다.
 * 비활성(거부 피드백) 클릭은 호출하지 않는다. 미주입 시 no-op(안전).
 *
 * ⚠ 전역(모듈) 단일 상태 — 멀티 Phaser.Scene 구성 시 마지막 설정자가 이전 것을 덮으므로, 씬별로
 * set/null 쌍을 호출해 소멸 씬의 콜백이 dangling 참조로 남지 않게 한다. 단일 씬 구조에서는
 * scene.restart마다 재주입(덮어쓰기)으로 충분(현 GameScene). 씬을 destroy하는 구성을 추가하면 그 시점에 null 해제.
 */
let clickSoundHook: (() => void) | null = null;

/** 전역 클릭 사운드 hook 설정(앱 부팅 1회). null 주입 시 해제. */
export function setNineSliceButtonClickSound(hook: (() => void) | null): void {
  clickSoundHook = hook;
}

/**
 * 표준 NineSlice 버튼 — `ButtonStyleToken` 토큰 기반.
 *
 * 책임:
 * - NineSlice 배경 + 텍스트 라벨 생성 (width/height/textStyle은 토큰에서, override 가능)
 * - **선택적 아이콘**: `iconKey` 지정 시 라벨 왼쪽에 아이콘(통화 등)을 두고 [아이콘+라벨]을 버튼 중앙 정렬한다.
 *   `setIcon(key)`로 텍스처 교체, `setIconTint(tint)`로 색상 적용, `setIconVisible(false)`로
 *   라벨만(예: "구매 완료") 표시.
 * - press scale tween (pointerdown/up/over/out) — DEFAULT_BUTTON_PRESS_TIMING 또는 constructor option `pressTiming` override
 * - **click 발화 시점은 `pointerup` 영역 안일 때만** (표준 touch UX). `pointerupoutside`(영역 밖 뗌)는 emit X (취소).
 *   `pointerout` 시 visual은 복귀하되 `_isPressed`는 유지 → drag-back 시 영역 안 재진입 후 떼면 emit O.
 * - 부분 갱신 메서드: `setLabel(text)` / `setIcon(key)` / `setIconTint(tint)` / `clearIconTint()` /
 *   `setIconVisible(bool)` / `setBgAlpha(alpha)` / `setBgTint(tint)` / `clearBgTint()` / `resetAlpha()`
 *   (destroy/recreate 0건)
 * - 거부 피드백: `playDenyFx(tint)` — 배경 틴트 플래시 + 좌우 흔들림 (불가 상태 클릭 신호. 색은 호출자 소유)
 * - 활성 상태: `setEnabled(bool)` — 비활성이면 click 미발화 + 스타일 토큰 `denyTint`(opt-in) 지정 시 자동 거부 피드백.
 *   시각 dim(알파)은 호출자 책임(`setBgAlpha`) — 앱 헬퍼가 둘을 묶는 패턴 권장.
 *
 * 사용 예:
 * ```ts
 * const btn = new NineSliceButton(scene, x, y, { style: BUTTON_STYLES.primary, label: "50", iconKey: "icon_gold" });
 * btn.on(NINE_SLICE_BUTTON_EVENT.Click, () => buy());
 * scene.add.existing(btn);
 * ```
 */
export class NineSliceButton extends Phaser.GameObjects.Container {
  private readonly bg: Phaser.GameObjects.NineSlice;
  private readonly label: Phaser.GameObjects.Text;
  private readonly icon?: Phaser.GameObjects.Image;
  private readonly iconSize: number;
  /** 아이콘과 라벨 사이 간격(px). */
  private static readonly ICON_GAP = 4;
  /** 아이콘 표시 여부 — setIconVisible로 토글(숨기면 라벨만 중앙). */
  private iconShown = true;
  private readonly style: ButtonStyleToken;
  private readonly pressTiming: ButtonPressTiming;
  private _isPressed = false;
  /** 활성 여부 — false면 click 미발화 + (denyTint 지정 시) 거부 피드백. 시각 dim은 호출자(setBgAlpha) 책임. */
  private enabled = true;
  /** 지속 배경 tint — disabled 등 상태 tint. deny 피드백 tint가 끝나면 이 tint로 복원된다. */
  private bgTint?: number;
  /**
   * 현재 pointerdown으로 눌린 상태인지. pointerout으로 영역을 벗어나도 유지 (drag-back 후 영역 안에서
   * pointerup 시 click 발화 허용). pointerup 또는 pointerupoutside 시 false로 전환.
   */
  get isPressed(): boolean {
    return this._isPressed;
  }

  constructor(
    scene: Phaser.Scene,
    x: number,
    y: number,
    options: {
      readonly style: ButtonStyleToken;
      readonly label: string;
      readonly widthOverride?: number;
      readonly heightOverride?: number;
      readonly pressTiming?: ButtonPressTiming;
      /** 라벨 왼쪽에 둘 아이콘 텍스처 키 (통화 등). 미지정 시 라벨만 중앙. */
      readonly iconKey?: string;
      /** 아이콘 표시 크기(px). 미지정 시 버튼 높이의 절반. */
      readonly iconSize?: number;
    },
  ) {
    super(scene, x, y);
    this.style = options.style;
    this.pressTiming = options.pressTiming ?? DEFAULT_BUTTON_PRESS_TIMING;
    const width = options.widthOverride ?? options.style.width;
    const height = options.heightOverride ?? options.style.height;
    const border = options.style.border;

    this.bg = scene.add.nineslice(
      0,
      0,
      options.style.textureKey,
      undefined,
      width,
      height,
      border.left,
      border.right,
      border.top,
      border.bottom,
    );
    this.bg.setOrigin(0.5, 0.5);
    this.bg.name = "nsBtnBg"; // dev QA hit-test 식별용
    this.bg.setInteractive({ useHandCursor: true });

    this.bg.on("pointerdown", (
      _pointer: Phaser.Input.Pointer,
      _lx: number,
      _ly: number,
      event: Phaser.Types.Input.EventData,
    ) => {
      event.stopPropagation();
      this._isPressed = true;
      this.playPressOn();
    });
    this.bg.on("pointerup", () => {
      if (!this._isPressed) return;
      this._isPressed = false;
      this.playPressOff();
      // 비활성 상태 — click 미발화. denyTint(스타일 토큰 opt-in)가 있으면 거부 피드백 재생.
      if (!this.enabled) {
        if (this.style.denyTint !== undefined) this.playDenyFx(this.style.denyTint);
        return;
      }
      // 유효 클릭 사운드 — 전역 hook(DIP, 앱이 주입). 핸들러 throw가 click emit을 막지 않도록 보호.
      if (clickSoundHook !== null) {
        try {
          clickSoundHook();
        } catch (err) {
          console.warn(`[NineSliceButton] click sound hook failed: ${(err as Error).message}`);
        }
      }
      this.emit(NINE_SLICE_BUTTON_EVENT.Click);
    });
    this.bg.on("pointerupoutside", () => {
      this._isPressed = false;
      this.playPressOff();
    });
    this.bg.on("pointerout", () => {
      this.playPressOff();
    });
    this.bg.on("pointerover", () => {
      if (this._isPressed) this.playPressOn();
    });
    this.add(this.bg);

    this.iconSize = options.iconSize ?? Math.floor(height * 0.5);
    if (options.iconKey) {
      this.icon = scene.add
        .image(0, 0, options.iconKey)
        .setDisplaySize(this.iconSize, this.iconSize);
      this.add(this.icon);
    }

    this.label = scene.add
      .text(0, 0, options.label, options.style.textStyle)
      .setOrigin(0.5, 0.5);
    this.add(this.label);
    this.layoutContent();

    if (options.style.alphaIdle !== undefined) {
      this.bg.setAlpha(options.style.alphaIdle);
    }

    this.setSize(width, height);
  }

  /** [아이콘+라벨] 그룹을 버튼 중앙 정렬 (SegmentedTabBar 패턴). 아이콘 없거나 숨김이면 라벨만 중앙. */
  private layoutContent(): void {
    if (!this.icon || !this.iconShown) {
      this.label.setPosition(0, 0);
      return;
    }
    const groupW = this.iconSize + NineSliceButton.ICON_GAP + this.label.width;
    const left = -groupW / 2;
    this.icon.setPosition(left + this.iconSize / 2, 0);
    this.label.setPosition(
      left + this.iconSize + NineSliceButton.ICON_GAP + this.label.width / 2,
      0,
    );
  }

  /** 라벨 텍스트만 교체 + [아이콘+라벨] 재정렬 (Text destroy/recreate 0건). */
  setLabel(text: string): void {
    this.label.setText(text);
    this.layoutContent();
  }

  /** 아이콘 텍스처 동적 교체 (통화별 아이콘 등). iconKey 미지정 생성 시 no-op. */
  setIcon(textureKey: string): void {
    if (!this.icon) return;
    this.icon.setTexture(textureKey).setDisplaySize(this.iconSize, this.iconSize);
    this.layoutContent();
  }

  /** 아이콘 tint 적용. 흰 실루엣 아이콘을 버튼 맥락 색으로 칠할 때 사용한다. */
  setIconTint(tint: number): void {
    this.icon?.setTint(tint);
  }

  /** 아이콘 tint 제거. iconKey 미지정 생성 시 no-op. */
  clearIconTint(): void {
    this.icon?.clearTint();
  }

  /** 아이콘 표시/숨김 — 숨기면 라벨만 중앙(예: 구매 완료/MAX 상태). iconKey 미지정 생성 시 no-op. */
  setIconVisible(visible: boolean): void {
    if (!this.icon) return;
    this.iconShown = visible;
    this.icon.setVisible(visible);
    this.layoutContent();
  }

  /** 배경 NineSlice 알파만 변경. dim/active 상태 토글 등에 사용. */
  setBgAlpha(alpha: number): void {
    this.bg.setAlpha(alpha);
  }

  /** 배경 NineSlice tint만 변경. disabled 등 지속 상태 색 전환에 사용. */
  setBgTint(tint: number): void {
    this.bgTint = tint;
    this.bg.setTint(tint);
  }

  /** 배경 NineSlice tint를 제거. deny 피드백 진행 중이면 완료/취소 시 clear 상태로 복원된다. */
  clearBgTint(): void {
    this.bgTint = undefined;
    this.bg.clearTint();
  }

  /** 진행 중인 거부 피드백 tween — counter 기반이라 press tween의 killTweensOf(this)에 죽지 않는다. */
  private denyTween?: Phaser.Tweens.Tween;

  /**
   * 거부(불가) 피드백 — 배경을 tint로 칠하고 좌우로 흔든 뒤 원상 복원 (±6px sin 3사이클, 360ms).
   * 비활성 상태 버튼을 눌렀을 때 "안 된다"는 시각 신호용. 색은 호출자 소유(테마 토큰 전달 — 패키지 하드코딩 금지).
   * tween 대상을 counter로 두는 이유: targets=this면 pointerdown의 killTweensOf(this)가 중간에 죽여
   * x 오프셋·tint가 잔존한다. 진행 중 재호출은 위치 복원 후 재시작.
   */
  playDenyFx(tint: number): void {
    const offsetPx = 6;
    this.cancelDenyFx(); // 진행 중이면 위치·tint 복원 후 재시작 (x 누적 오프셋 방지)
    const baseX = this.x;
    this.denyBaseX = baseX;
    this.bg.setTint(tint);
    this.denyTween = this.scene.tweens.addCounter({
      from: 0,
      to: 1,
      duration: 360,
      ease: "Linear",
      onUpdate: (tw) => {
        if (!this.active) return;
        this.setX(baseX + Math.sin((tw.getValue() ?? 0) * Math.PI * 6) * offsetPx);
      },
      onComplete: () => {
        if (this.active) {
          this.setX(baseX);
          this.restoreBgTint();
        }
        this.denyTween = undefined;
        this.denyBaseX = undefined;
      },
    });
  }

  /** playDenyFx 시작 시점의 기준 x — 진행 중 재호출·중단 시 복원용. */
  private denyBaseX?: number;

  /** 진행 중 거부 피드백 중단 + 위치·tint 원상 복원 (재호출·활성 복귀·destroy 공용). */
  private cancelDenyFx(): void {
    if (!this.denyTween) return;
    this.denyTween.remove();
    this.denyTween = undefined;
    if (this.denyBaseX !== undefined) {
      this.setX(this.denyBaseX);
      this.denyBaseX = undefined;
    }
    this.restoreBgTint();
  }

  /** 일시 deny tint 종료 후 지속 상태 tint(disabled 등)를 복원한다. */
  private restoreBgTint(): void {
    if (this.bgTint !== undefined) {
      this.bg.setTint(this.bgTint);
      return;
    }
    this.bg.clearTint();
  }

  /** scene tween은 Container destroy로 자동 정리되지 않으므로 거부 피드백 tween을 직접 제거. */
  override destroy(fromScene?: boolean): void {
    this.cancelDenyFx();
    super.destroy(fromScene);
  }

  override setVisible(visible: boolean): this {
    super.setVisible(visible);
    (this as Phaser.GameObjects.GameObject & { setActive?: (value: boolean) => unknown }).setActive?.(
      visible,
    );
    this.setBgInputEnabled(visible);
    if (!visible) {
      this._isPressed = false;
      this.scene.tweens.killTweensOf(this);
      this.setScale(1);
    }
    return this;
  }

  /** 배경 NineSlice 알파를 토큰의 `alphaIdle`(미정 시 1.0) 으로 복원. */
  resetAlpha(): void {
    this.bg.setAlpha(this.style.alphaIdle ?? 1.0);
  }

  /**
   * 활성/비활성 전환 — 비활성이면 click이 발화하지 않고, 스타일 토큰에 `denyTint`가 있으면
   * 클릭 시 거부 피드백(playDenyFx)을 재생한다. press 스케일 애니는 유지(눌림 촉감은 보존).
   * 시각 dim(알파)은 정책이 호출자 소유라 함께 바꾸지 않는다 — 앱 헬퍼(setButtonEnabled)가 둘을 묶는다.
   */
  setEnabled(enabled: boolean): void {
    this.enabled = enabled;
    // 활성 복귀 시 진행 중 거부 피드백 잔존(활성 버튼이 흔들리며 tint 유지) 방지.
    if (enabled) this.cancelDenyFx();
  }

  get isEnabled(): boolean {
    return this.enabled;
  }

  private setBgInputEnabled(enabled: boolean): void {
    if (this.bg.input) {
      this.bg.input.enabled = enabled;
      return;
    }
    if (enabled) this.bg.setInteractive({ useHandCursor: true });
  }

  private playPressOn(): void {
    this.scene.tweens.killTweensOf(this);
    this.scene.tweens.add({
      targets: this,
      scale: this.pressTiming.pressedScale,
      duration: this.pressTiming.pressOnDurationMs,
      ease: this.pressTiming.pressOnEase,
    });
  }

  private playPressOff(): void {
    this.scene.tweens.killTweensOf(this);
    this.scene.tweens.add({
      targets: this,
      scale: 1,
      duration: this.pressTiming.pressOffDurationMs,
      ease: this.pressTiming.pressOffEase,
    });
  }
}
